<script>
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';
  import { format as formatDate, formatDistanceToNowStrict } from 'date-fns';
  import { unparse } from 'papaparse';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';

  import { DomainService } from '@client/services/domain';
  import { DomainsService } from '@client/services/domains';

  import { appStore } from '@client/stores/app';
  import { billingId } from '@client/stores/domain-settings';
  import { switchDomain } from '@client/stores/domain-switch';
  import { userStore } from '@client/stores/user';

  import { downloadFile } from '@client/utils/download';
  import { calculateUsage } from '@client/utils/usage';

  import { Features } from './features';
  import { Integrations } from './integrations';
  import { KPIS } from './kpis';
  import { CloneDomainModal, UpdateBillingModal } from './modals';
  import { SPF } from './spf';
  import { Usage } from './usage';

  export let domain = null;
  export let selected = null;
  export let usage = null;
  export let panel = null;
  export let onBillingIdClick = () => {};

  let showMenu = false;
  let showExportsMenu = false;
  let showUpdateBillingModal = false;
  let showCloneDomainModal = false;

  const cache = {};
  const navigate = useNavigate();

  const { addNotification } = getNotificationsContext();

  const redirect = () => !panel && navigate(domain.domainname);

  const onManageClick = async () => {
    const result = await switchDomain(domain.domainname);

    if (!result.ok && result.reason !== 'superseded') {
      const label = result.reason === 'not-eligible' ? 'label.SWITCH_DOMAIN_NOT_ALLOWED' : 'label.SWITCH_DOMAIN_FAILED';

      addNotification({
        text: $format(label, { values: { domain: domain.domainname } }),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    }
  };

  const onClearDomainCache = async () => await DomainsService.clearDomainCache({ domainname: domain.domainname });

  const requestSpfStatus = async ({ domainname }) => {
    if (!cache[domainname]?.spf) {
      cache[domainname].spf = await DomainsService.getSPFReport({ domainname });
    }

    return cache[domainname].spf;
  };

  const requestAcitvityStats = async ({ domainname }) => {
    if (!cache[domainname]?.cs) {
      const response = await DomainsService.getCSReport({ domainname });

      if (!Array.isArray(response)) {
        cache[domainname].cs = {
          users: 0,
          user_count: [],
          tagblockchanges: 0,
          adchanges: 0,
          spark: { labels: [], datasets: [] }
        };
        return cache[domainname].cs;
      }

      const data = {
        users: 0,
        user_count: [],
        tagblockchanges: 0,
        adchanges: 0
      };

      response
        .sort((a, b) => a.timeslice > b.timeslice)
        .forEach((item) => {
          const ts = new Date(item.timeslice).getTime();
          data.user_count.push([ts, item.user_count]);
          data.users = item.user_count;

          data.tagblockchanges += item.tagblock_updates;
          data.tagblockchanges += item.tagblock_deletes;
          data.tagblockchanges += item.tagblock_creates;

          data.adchanges += item.ad_updates;
          data.adchanges += item.ad_deletes;
          data.adchanges += item.ad_creates;
        });

      const labels = [];
      const points = [];

      data.user_count.forEach(([label, point]) => {
        labels.push(formatDate(label, 'EEE, MMM d, yyyy'));
        points.push(point);
      });

      data.spark = {
        labels,
        datasets: [
          {
            label: $format('label.ACTIVE_USERS'),
            backgroundColor: 'rgba(225, 204,230, .1)',
            borderColor: 'rgb(0, 135, 255)',
            pointColor: 'rgb(0, 135, 255)',
            fill: true,
            tension: 0.3,
            data: points
          }
        ]
      };

      cache[domainname].cs = data;
    }

    return cache[domainname].cs;
  };

  const requestDomainSettings = async ({ domainname }) => {
    if (!cache[domainname]) {
      cache[domainname] = {};
    }

    if (!cache[domainname].domain) {
      cache[domainname].domain = await DomainsService.getDomainReport({ domainname });
    }

    requestSpfStatus({ domainname });
    requestAcitvityStats({ domainname });

    return cache[domainname].domain.domainsettings;
  };

  const onEditBilling = () => {
    showUpdateBillingModal = true;
  };

  const onCloneDomain = () => {
    showCloneDomainModal = true;
  };

  const onExportBillingIdUsage = async () => {
    $appStore.loading = true;

    let data = null;

    try {
      data = await DomainService.getBillingIdUsage({ billingid: domain.domainsettings.billing_id });
    } catch (error) {
      $appStore.loading = false;
      return;
    }

    let columns = {
      email: 1,
      tier: 1
    };

    data.forEach((row) => {
      Object.keys(row).forEach((k) => {
        if (!columns[k]) {
          columns[k] = 1;
        }
      });
    });

    const text = unparse(data, { columns: Object.keys(columns) });
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const today = formatDate(new Date(), 'M-dd-yyyy');

    $appStore.loading = false;

    downloadFile(blob, `billing-usage-${domain.domainsettings.billing_id}-${today}.csv`);
  };

  const onExportUserUsage = async () => {
    $appStore.loading = true;

    const data = await DomainService.getUsage(domain);

    let columns = {
      email: 1,
      tier: 1
    };

    data.forEach((row) => {
      Object.keys(row).forEach((k) => {
        if (!columns[k]) {
          columns[k] = 1;
        }
      });
    });

    const text = unparse(data, { columns: Object.keys(columns) });
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const today = formatDate(new Date(), 'M-dd-yyyy');

    $appStore.loading = false;

    downloadFile(blob, `user-usage-${domain.domainsettings.billing_id}-${domain.domainname}-${today}.csv`);
  };

  const onExportAllUsage = () => {
    const calucatedUsage = calculateUsage(usage);
    const details = cache[domain.domainname]?.domain;

    const data = [
      {
        domains: usage.domains.join(', '),
        billing_id: domain.domainsettings.billing_id,

        signature_used: calucatedUsage.signature.usage.toLocaleString(),
        signature_sold: calucatedUsage.signature.sold.toLocaleString(),
        signature_overage: calucatedUsage.signature.overage.toLocaleString(),
        signature_underage: calucatedUsage.signature.underage.toLocaleString(),

        pipeline_used: calucatedUsage.pipeline.usage.toLocaleString(),
        pipeline_sold: calucatedUsage.pipeline.sold.toLocaleString(),
        pipeline_overage: calucatedUsage.pipeline.overage.toLocaleString(),
        pipeline_underage: calucatedUsage.pipeline.underage.toLocaleString(),

        complete_used: calucatedUsage.complete.usage.toLocaleString(),
        complete_sold: calucatedUsage.complete.sold.toLocaleString(),
        complete_overage: calucatedUsage.complete.overage.toLocaleString(),
        complete_underage: calucatedUsage.complete.underage.toLocaleString(),

        engagement_used: usage.engagement.toLocaleString(),

        total_sold: calucatedUsage.totals.totalSold.toLocaleString(),
        total_usage: calucatedUsage.totals.totalUsage.toLocaleString(),
        total_usage_percent: calucatedUsage.totals.totalUsagePercentage.toLocaleString(),
        total_overage: calucatedUsage.totals.totalOverage.toLocaleString(),
        total_overage_percent: calucatedUsage.totals.totalOverage.toLocaleString(),
        total_underage: calucatedUsage.totals.totalUnderage.toLocaleString(),

        signature_changes: usage.tagblockchanges.toLocaleString(),
        ad_changes: usage.adchanges.toLocaleString(),
        last_user_login: details
          ? details?.lastLogin?.length
            ? `${details.lastLogin[0].user}|${details.lastLogin[0].timestamp}|${details.lastLogin[0].user}`
            : ''
          : '',
        last_user_activity: details
          ? details?.lastUpdate?.length
            ? `${details.lastUpdate[0].user}|${details.lastUpdate[0].timestamp}|${details.lastLogin[0]?.type}`
            : ''
          : '',
        report_date: usage.reportDate
      }
    ];

    const text = unparse(data);
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const today = formatDate(new Date(), 'M-dd-yyyy');

    downloadFile(blob, `usage-${domain.domainsettings.billing_id}-${domain.domainname}-${today}.csv`);
  };

  const toggleExportsMenu = (e) => {
    showExportsMenu = !showExportsMenu;
  };

  const closeExportsMenu = (e) => {
    const target = e.target.closest('#export-dropdown');

    if (!target) {
      showExportsMenu = false;
    }
  };

  $: requestDomainSettings(domain);
  $: isStaffTools = location.href.indexOf('staff/domains') > -1;
  $: cached = cache[domain.domainname] || {};
  $: domainname = domain?.domainname?.split('.').shift();
  $: domainsettings = cached?.domain?.domainsettings || {};
</script>

<div
  class="domain-card"
  class:panel
  class:selected
  role="button"
  tabindex="0"
  data-testid="domain-card-{domainname}"
  on:mouseleave={() => (showMenu = false)}
>
  <div class="domain">
    <div>
      <span role="button" tabindex="-1" on:click={redirect}>{domain.domainname}</span>
      {#if domain.domainsettings?.billing_id}
        <small role="button" tabindex="0" on:click={() => onBillingIdClick(domain.domainsettings?.billing_id)}
          >{$format('label.BILLING_ID_DOMAIN_CARD', { values: { id: domain.domainsettings.billing_id } })}</small
        >
      {:else}
        <small>{$format('label.SET_BILLING_ID')}</small>
        {#if domain.domainsettings?.billing_customer_id}
          <small>(Maxio {domain.domainsettings.billing_customer_id})</small>
        {/if}
      {/if}

      {#if $userStore.isStaff}
        <Icon name="edit-2" role="button" tabindex="0" on:click={onEditBilling} class="text-small" />
      {/if}

      {#if domainsettings?.disableservice}
        <span class="disabled">{$format('label.SUSPENDED')}</span>
      {/if}

      {#if cached?.domain?.lastLogin?.length}
        {@const lastLogin = cached.domain.lastLogin[0]}
        <div class="last-activity">
          {@html $format('label.LAST_LOGIN_WAS_BY', {
            values: {
              time: formatDistanceToNowStrict(new Date(lastLogin.timestamp), {
                addSuffix: true
              }),
              user: lastLogin.user.split('@').shift()
            }
          })}
        </div>
      {:else if cached?.domain?.lastUpdate?.length}
        {@const lastUpdate = cached.domain.lastUpdate[0]}
        <div class="last-activity">
          {@html $format('label.LAST_UPDATE_WAS_BY', {
            values: {
              time: formatDistanceToNowStrict(new Date(lastUpdate.timestamp), {
                addSuffix: true
              }),
              user: lastUpdate.user.split('@').shift()
            }
          })}
        </div>
      {/if}

      <div class="cname" class:warning={domainsettings?.cname?.endsWith('hosted-content.com')}>
        {#if cached?.domain}
          {#if domainsettings.cname.endsWith('hosted-content.com')}
            <Icon name="alert-triangle" id="cname-warning" />
            <Tooltip target="cname-warning" placement="top">
              {$format('label.DOMAIN_NOT_USING_SMART', { values: { domain: domain.domainname } })}
            </Tooltip>
          {/if}
          {domainsettings.cnamessl ? 'https' : 'http'}://{domainsettings.cname}
        {:else}
          &nbsp;
        {/if}
      </div>

      {#if cached?.domain}
        {#if !domainsettings.office365hostname && domainsettings.office365enabled}
          <div class="hostname error">
            <Icon name="alert-circle" id="hostname-warning" />
            <Tooltip target="hostname-warning" placement="top"
              >{$format('label.TOOLTIP.MICROSOFT_HOSTNAME_MISSING')}</Tooltip
            >
            {$format('label.MICROSOFT_HOSTNAME_MISSING')}
          </div>
        {/if}
      {/if}
    </div>

    {#if !panel}
      <div class="action">
        <Dropdown data-testid="dropdown-actions-{domainname}" isOpen={showMenu} toggle={() => (showMenu = !showMenu)}>
          <DropdownToggle tag="div" class="os-icon-more-horizontal" />
          <DropdownMenu>
            {#if !selected}
              <DropdownItem data-testid="manage-domain-{domainname}" on:click={onManageClick}>
                {$format('label.MANAGE_DOMAIN')}
              </DropdownItem>
              <DropdownItem divider />
            {/if}
            <DropdownItem on:click={redirect}>{$format('label.VIEW_DETAILS')}</DropdownItem>
            {#if $userStore.isStaff}
              <DropdownItem on:click={onCloneDomain}>{$format('label.CLONE_DOMAIN')}</DropdownItem>
            {/if}
            <DropdownItem divider />
            <DropdownItem on:click={onClearDomainCache}>
              {$format('label.CLEAR_CACHE')}
            </DropdownItem>
            <DropdownItem divider />
            <Dropdown id="export-dropdown" isOpen={showExportsMenu} toggle={toggleExportsMenu} direction="right">
              <DropdownToggle tag="div" class="d-block">
                <div
                  class="flex px-4 py-3 justify-content-center align-items-center"
                  on:mouseenter={toggleExportsMenu}
                  on:mouseleave={closeExportsMenu}
                  role="button"
                  tabindex="0"
                >
                  <div>{$format('label.EXPORT')}</div>
                  <Icon name="arrow-right" style="font-size: var(--font-size-base)" />
                </div>
              </DropdownToggle>
              <DropdownMenu>
                {#if usage}
                  <DropdownItem on:click={onExportAllUsage}>
                    {$format('label.EXPORT_ALL_USAGE')}
                  </DropdownItem>
                {/if}

                <DropdownItem on:click={onExportUserUsage}>
                  {$format('label.EXPORT_USER_USAGE')}
                </DropdownItem>

                {#if $userStore.isStaff && domain?.domainsettings?.billing_id}
                  <DropdownItem on:click={onExportBillingIdUsage}>
                    {$format('label.EXPORT_BILLING_ID_USAGE')}
                  </DropdownItem>
                {/if}
              </DropdownMenu>
            </Dropdown>
          </DropdownMenu>
        </Dropdown>
      </div>
    {/if}
  </div>

  <div class="details" role="button" tabindex="-1" on:click={redirect}>
    <KPIS data={cached.cs} domain={cached.domain} />
    <div class="settings">
      <div class="suite">
        {#if cached?.domain}
          {#if domainsettings.googleenabled}
            <img src="https://smart.opensense.com/v2/imagebucket/opensense.com/google-emblem.png" alt="Google Logo" />
          {:else}
            <img
              src="https://smart.opensense.com/v2/imagebucket/opensense.com/microsoft-emblem.png"
              alt="Microsoft Logo"
            />
          {/if}
        {/if}
      </div>
      <div class="badges" on:click|preventDefault|stopPropagation role="button" tabindex="0">
        {#key domain.domainname}
          {#if usage}
            <Usage {domain} {usage} />
          {/if}
          {#if cached?.spf}
            <SPF {domain} spf={cached.spf} />
          {/if}
          {#if cached?.domain}
            <Integrations domain={cached.domain} />
          {/if}
          {#if cached?.domain}
            <Features domain={cached.domain} />
          {/if}
        {/key}
      </div>
    </div>
  </div>
</div>

{#if showUpdateBillingModal}
  <UpdateBillingModal bind:show={showUpdateBillingModal} bind:domain />
{/if}

{#if showCloneDomainModal}
  <CloneDomainModal bind:show={showCloneDomainModal} bind:domain />
{/if}

<style lang="scss" src="./domain-card.scss"></style>
